import MultiSelectInputMUI from '@brands/Components/Inputs/MuiMultiSelectInput/MultiSelectInputMUI';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import CustomTextField from '@brands/Components/Inputs/MuiTextField/TextField';
import MuiLongTextField from '@brands/Components/Inputs/MuiLongTextField/MuiLongTextField';
import AddEmailModal from '@brands/Dashboard/Dashboard/SuperAdmin/Components/AddEmailModal/AddEmailModal';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useDivisionScopeActor } from '@brands/hooks/useDivisionScopeActor';
import { activateUserById } from '@brands/services/identity/activateUserById';
import { getCurrentProfile } from '@brands/services/identity/getCurrentProfile';
import { getUserById } from '@brands/services/identity/getUserById';
import { reinviteUserById } from '@brands/services/identity/reinviteUserById';
import { setUser } from '@brands/store/slices/authSlice';
import { setCareAdvisorMyProfile } from '@brands/store/slices/careAdvisorMyProfile';
import { yupResolver } from '@hookform/resolvers/yup';
import HttpsOutlinedIcon from '@mui/icons-material/HttpsOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import ConfirmationModal from '@brands/Components/Modals/ConfirmationModal/ConfirmationModal';
import useUnlockAccount from '@brands/Dashboard/MyProfile/Components/useUnlockAccount';
import { Alert, Button, Chip, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { E164Number, parsePhoneNumber } from 'libphonenumber-js';
import { isEqual } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FieldErrors, useForm } from 'react-hook-form';
import { IoAdd, IoChevronBack, IoChevronDown, IoEllipsisVertical } from 'react-icons/io5';
import { toast } from 'react-toastify';

import ProfileImage, { type ProfileImageGivingUserProfile } from '../../../../Components/ProfileImage/ProfileImage';
import { useAppDispatch } from '../../../../hooks/useReduxHook';
import { RegisterMediaPayload } from '../../../../services/communication/registersConversationMedia';
import { confirmAvatarMedia } from '../../../../services/identity/confirmAvatarMedia';
import { createBasicInformation } from '../../../../services/identity/createBasicInformation';
import { createCareAdvisorProfile } from '../../../../services/identity/createCareAdvisorProfile';
import { deleteAvatar } from '../../../../services/identity/deleteAvatar';
import { registerAvatarMedia } from '../../../../services/identity/registerAvatarMedia';
import { saveBasicInformation } from '../../../../services/identity/saveBasicInformation';
import { saveCareAdvisorProfile } from '../../../../services/identity/saveCareAdvisorProfile';
import { Address, BasicInfo, UserProfile } from '../../../../services/identity/types/UserProfile';
import { updateMe } from '../../../../services/identity/updateMe';
import { displayErrorDetails } from '../../../../Utils/displayError';
import { OptionLanguage } from '../../../../Utils/selectOptions';
import addNewUserStyles from '../../../Dashboard/Collector/AddNewUser/addNewUser.module.scss';
import collectorShellStyles from '../../../Dashboard/Collector/EditUser/collectorEditUser.module.scss';
import { CAMyProfileSubmit } from '../utils/types';
import { validationSchema } from '../utils/validationSchema';
import { hasBasicInfoChanged, hasCareAdvisorProfileChanged, hasContactInfoChanged } from './helpers';
import styles from './styles.module.scss';
import { ClientTag, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import { OrganizationAdminMyProfileSubmit } from '../../OrganizationAdmin/utils/types';
import { updateVerifiedEmail } from '@brands/services/identity/updateUserEmail';
import { updateUserClientTag } from '@brands/services/identity/updateUserClientTag';
import {
  ClientDivisionGroup,
  getAllowedDivisionIds,
  getAssignedClientDivisionGroups,
  getClientDivisionOptionGroups,
  hasUnfilledRequiredDivisionGroup,
  scopeDivisionGroupsToClientAdmin,
} from '@brands/services/identity/clientDivisionOptions';
import { getNetworkLabel, isNetworkManagedByD1, NetworkOption } from '@brands/services/identity/networkOptions';
import { updateUserNetwork } from '@brands/services/identity/updateUserNetwork';
import { ClientTagOption } from '../MyProfile';

type EditProfileProps = {
  formRef: React.RefObject<HTMLFormElement>;
  setIsDirty: (value: boolean) => void;
  myProfileForm: CAMyProfileSubmit;
  setMyProfileForm: React.Dispatch<React.SetStateAction<CAMyProfileSubmit>>;
  setImage: React.Dispatch<React.SetStateAction<string | undefined>>;
  image: string | undefined;
  setCurrentAvatarURL: React.Dispatch<React.SetStateAction<string>>;
  currentAvatarURL: string;
  thisUser: UserProfile;
  setThisUser: React.Dispatch<React.SetStateAction<UserProfile>>;
  avatarAction: string;
  setAvatarAction: React.Dispatch<React.SetStateAction<string>>;
  reviewProfile: boolean | undefined;
  languageOptions: OptionLanguage[];
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isSavingProfile: boolean;
  setIsSavingProfile: React.Dispatch<React.SetStateAction<boolean>>;
  userId: string;
  resetEmailSent: boolean;
  setResetPasswordConfirmation: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      userState: string;
    }>
  >;
  setChangeUserStatusFlag: React.Dispatch<React.SetStateAction<string>>;
  clientTagOptions?: ClientTagOption[];
  clientTagCatalog?: ClientTag[];
  networkOptions?: NetworkOption[];
};

const EditProfile = ({
  formRef,
  setIsDirty,
  myProfileForm,
  setMyProfileForm,
  setImage,
  image,
  setCurrentAvatarURL,
  currentAvatarURL,
  thisUser,
  setThisUser,
  avatarAction,
  setAvatarAction,
  reviewProfile,
  languageOptions,
  setLoading,
  isSavingProfile,
  setIsSavingProfile,
  userId,
  resetEmailSent,
  setResetPasswordConfirmation,
  setChangeUserStatusFlag,
  clientTagOptions = [],
  clientTagCatalog = [],
  networkOptions = [],
}: EditProfileProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useRoleNavigate();
  const [avatarPayload, setAvatarPayload] = useState<RegisterMediaPayload | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [userHeaderMenuAnchor, setUserHeaderMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [isInvitedUser, setIsInvitedUser] = useState(false);
  const [displayEmailModal, setDisplayEmailModal] = useState(false);

  const { userInfo } = useAppSelector(selectAuth);
  const divisionScopeActor = useDivisionScopeActor();
  const {
    showUnlockAccount,
    isUnlocking,
    unlockConfirmOpen,
    openUnlockConfirm,
    closeUnlockConfirm,
    handleUnlockAccount,
  } = useUnlockAccount(thisUser, setThisUser);
  // Required for every role once the field is actually on the page (mirrors the
  // `clientTagOptions.length > 0` visibility check on the Client tag select below) --
  // requiredness used to be gated to CareAdvisor/Collector/OrganizationAdmin only.
  const clientTagsRequired = useMemo(() => clientTagOptions.length > 0, [clientTagOptions]);
  const canEditNetwork = userInfo.role.name === UserRoleName.Admin || userInfo.role.name === UserRoleName.SuperAdmin;
  const isReadOnly = userInfo.role.name === UserRoleName.CareAdvisor;
  /**
   * A Client Admin can view, but never manage, a Care Advisor who carries a D1 Network marker:
   * every field is disabled and the account actions (status, invite, password) are hidden.
   */
  const isManagedByD1 = isNetworkManagedByD1(userInfo.role.name, thisUser.network);
  const {
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
    control,
    watch,
    reset,
  } = useForm<CAMyProfileSubmit>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema(clientTagsRequired, reviewProfile)),
  });

  const watchedFields = watch();

  const divisionGroups = useMemo(
    () =>
      scopeDivisionGroupsToClientAdmin(
        getClientDivisionOptionGroups(
          clientTagCatalog,
          watchedFields.client_tag_ids || [],
          (thisUser.divisions || []).map((d) => ({ id: String(d.id), value: d.id, label: d.name })),
        ),
        clientTagCatalog,
        divisionScopeActor,
      ),
    [clientTagCatalog, watchedFields.client_tag_ids, thisUser.divisions, divisionScopeActor],
  );

  // A Client Admin never changes a user's client tags, so the tag field is read-only for them and
  // the save sends the tags back as the server holds them; divisions under their own tag stay editable.
  const clientTagsLocked = Boolean(reviewProfile) && userInfo.role.name === UserRoleName.OrganizationAdmin;

  const assignedDivisionGroups = useMemo(
    () => getAssignedClientDivisionGroups(clientTagCatalog, thisUser.divisions || []),
    [clientTagCatalog, thisUser.divisions],
  );

  // Each picker owns only its own tag's ids so the field stays the flat list the payload wants.
  const setGroupDivisionIds = (group: ClientDivisionGroup, ids: string[]): void => {
    const groupIds = new Set(group.options.map((option) => String(option.id)));

    setValue(
      'client_division_ids',
      [...(watch('client_division_ids') || []).filter((id) => !groupIds.has(String(id))), ...ids],
      { shouldValidate: true },
    );
  };

  const isHydratingFormRef = useRef(false);
  const hasUserEditedFormRef = useRef(false);

  const triggerProfileAvatar = React.useCallback(
    (avatarUrl: string) => {
      isHydratingFormRef.current = true;
      try {
        setValue('profile_img', avatarUrl, { shouldValidate: true });
      } finally {
        isHydratingFormRef.current = false;
      }
      setMyProfileForm({
        ...getValues(),
        profile_img: avatarUrl,
      });
    },
    [setValue, getValues, setMyProfileForm],
  );

  const hasChanges = useMemo(() => {
    return !isEqual(myProfileForm, getValues()) || !isEqual(avatarAction, '');
  }, [myProfileForm, watchedFields, avatarAction]);

  /**
   * Whether the user has changed anything since the form was last hydrated or saved.
   * Comparing `myProfileForm` against live form values directly is unreliable here: a
   * successful save re-derives `myProfileForm` from the refetched user, which doesn't always
   * format fields identically to what's already in the form, so that comparison can
   * permanently disagree even with nothing left to save. Used only to drive the
   * unsaved-changes prompt; the Save button below keeps using `hasChanges` since a false
   * positive there is harmless (an idempotent re-save), unlike blocking navigation.
   */
  const [hasEditedSinceSave, setHasEditedSinceSave] = useState(false);

  useEffect(() => {
    setIsDirty(hasEditedSinceSave || avatarAction !== '');
  }, [hasEditedSinceSave, avatarAction, setIsDirty]);

  useEffect(() => {
    const subscription = watch(() => {
      if (!isHydratingFormRef.current) {
        hasUserEditedFormRef.current = true;
        setHasEditedSinceSave(true);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    hasUserEditedFormRef.current = false;
    setHasEditedSinceSave(false);
  }, [userId]);

  const clientTags = useMemo(() => {
    if (thisUser?.clients && thisUser.clients.length > 0) {
      return thisUser.clients.map((cli) => cli.name);
    }
    return [];
  }, [thisUser]);

  const headerDisplayName = useMemo(() => {
    return (
      thisUser?.full_name?.trim() ||
      [thisUser?.basic_info?.first_name, thisUser?.basic_info?.last_name].filter(Boolean).join(' ').trim() ||
      '—'
    );
  }, [thisUser?.basic_info?.first_name, thisUser?.basic_info?.last_name, thisUser?.full_name]);

  const collectorDisplayStatus = useMemo(() => {
    if ((thisUser as { deleted_at?: string })?.deleted_at) return 'Deactivated';
    return thisUser?.status?.trim() || 'Active';
  }, [thisUser]);

  const statusPillClass =
    collectorDisplayStatus === 'Active'
      ? collectorShellStyles.statusPillReadOnlyActive
      : collectorDisplayStatus === 'Deactivated'
      ? collectorShellStyles.statusPillReadOnlyDeactivated
      : collectorShellStyles.statusPillReadOnlyOther;

  const statusOptions = useMemo(() => {
    if (
      thisUser.role.name === UserRoleName.Provider &&
      collectorDisplayStatus !== 'Pending' &&
      collectorDisplayStatus !== 'Deactivated'
    ) {
      return [
        { id: 'pending', name: 'Pending' },
        { id: 'deactivated', name: 'Deactivated' },
      ];
    }
    if (thisUser.role.name === UserRoleName.Provider && collectorDisplayStatus === 'Pending') {
      return [
        { id: 'active', name: 'Active' },
        { id: 'deactivated', name: 'Deactivated' },
      ];
    }
    if (collectorDisplayStatus !== 'Deactivated') {
      return [{ id: 'deactivated', name: 'Deactivated' }];
    }
    return [{ id: 'active', name: 'Active' }];
  }, [collectorDisplayStatus, thisUser.role.name]);

  const canCreateCase = thisUser.role.name === UserRoleName.Patient || thisUser.role.name === UserRoleName.Dependent;

  const openUserHeaderMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setUserHeaderMenuAnchor(event.currentTarget);
  };

  const closeUserHeaderMenu = (): void => {
    setUserHeaderMenuAnchor(null);
  };

  const openStatusMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setStatusMenuAnchor(event.currentTarget);
  };

  const closeStatusMenu = (): void => {
    setStatusMenuAnchor(null);
  };

  const getInvitationDomain = (): string => {
    const org =
      thisUser?.organizations.find((organization) => organization.id !== 'dentistry-one') ??
      thisUser?.organizations.find((organization) => organization.id === 'dentistry-one');
    const currentDomain = window.location.hostname;
    const fallbackDomain =
      currentDomain === 'localhost' || currentDomain.startsWith('localhost') ? 'develop.dentistry.one' : currentDomain;

    if (!org) return fallbackDomain;

    const divisionDomain = org.divisions?.[0]?.domain;
    const practiceDomain = org.practices?.[0]?.domain;

    return divisionDomain || practiceDomain || fallbackDomain;
  };

  const inviteUser = async (): Promise<void> => {
    setIsInvitedUser(true);
    try {
      const updatedUser = await reinviteUserById(
        userId || '',
        { frontend_website: `https://${getInvitationDomain()}` },
        userInfo.role.name,
      );
      setThisUser({ ...thisUser, status: updatedUser?.status, email: updatedUser?.email });
    } catch (error: unknown) {
      const responseMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      if (responseMessage?.includes('User has no email') || responseMessage?.includes('12209-3')) {
        setDisplayEmailModal(true);
        return;
      }
      displayErrorDetails(error);
    } finally {
      setIsInvitedUser(false);
    }
  };

  const handleStatusClick = async (status: string): Promise<void> => {
    if (status === 'deactivated' || status === 'pending') {
      setChangeUserStatusFlag(status);
    } else {
      try {
        const userProfile = await activateUserById(Number(userId));
        setThisUser({ ...thisUser, status: userProfile?.status ?? thisUser.status, deleted_at: null });
        toast.success('User has been activated.', { className: 'fs-unmask' });
      } catch (error: unknown) {
        displayErrorDetails(error);
      }
    }
    closeStatusMenu();
  };

  const languageSelectOptions = useMemo(
    () =>
      languageOptions.map((lang) => ({
        id: lang.id.toString(),
        value: lang.value,
        label: lang.label,
      })),
    [languageOptions],
  );

  const selectedLanguageIds = useMemo(() => {
    const langValue = getValues('language');
    if (Array.isArray(langValue)) {
      return langValue.map((l: OptionLanguage) => l.id.toString());
    }
    return [];
  }, [watchedFields]);

  const callSaveProfile = async (profileData: CAMyProfileSubmit): Promise<void> => {
    setIsSavingProfile(true);
    const userRole = reviewProfile ? undefined : 'CareAdvisor';
    try {
      if (avatarPayload && avatarFile && avatarAction === 'Upload') {
        const res = await registerAvatarMedia(avatarPayload, userRole);
        const mediaId = res.files[0].upload_id;
        await fetch(res.files[0].upload_url, {
          method: 'PUT',
          body: avatarFile,
          headers: { 'content-type': avatarFile.type },
        });
        await confirmAvatarMedia(mediaId, thisUser.id, undefined, userRole);
        triggerProfileAvatar(res.files[0].upload_url);
        // The parent rebuilds the form from currentAvatarURL once the saved
        // user comes back. Left stale, it wiped profile_img after the first
        // save, and every later save failed with "Profile image is required".
        setCurrentAvatarURL(res.files[0].upload_url);
      } else if (avatarAction === 'Remove') {
        await deleteAvatar(thisUser.id, userRole);
        triggerProfileAvatar('');
        setCurrentAvatarURL('');
      }

      let updatedUser;

      if (
        userInfo.role.name !== UserRoleName.CareAdvisor &&
        thisUser?.email !== (profileData as CAMyProfileSubmit).email
      ) {
        updatedUser = await updateVerifiedEmail(thisUser?.id || 0, {
          email: (profileData as CAMyProfileSubmit).email as string,
        });
      }

      if (hasContactInfoChanged(thisUser, profileData)) {
        const { zip_code } = profileData;
        const userAddress: Address = { zip_code };
        updatedUser = await updateMe(
          {
            phone_number: parsePhoneNumber(profileData.phone_number, 'US')?.format('E.164') || null,
            address: userAddress,
          },
          thisUser.id,
          userRole,
        );
      } else {
        updatedUser = thisUser;
      }

      const basicInfoPayload: BasicInfo = {
        ...(profileData as BasicInfo),
        languages: profileData.language.map((lang) => lang.id.toString()),
      };

      if (hasBasicInfoChanged(thisUser, basicInfoPayload)) {
        if (thisUser.basic_info !== null) {
          updatedUser = await saveBasicInformation(basicInfoPayload, thisUser.id, userRole);
        } else {
          updatedUser = await createBasicInformation(basicInfoPayload, thisUser.id, userRole);
        }
      }

      const caProfilePayload = {
        biography: profileData.biography,
      };

      if (
        !thisUser.care_advisor_profile ||
        hasCareAdvisorProfileChanged(thisUser.care_advisor_profile, caProfilePayload)
      ) {
        updatedUser = thisUser.care_advisor_profile
          ? await saveCareAdvisorProfile(caProfilePayload, thisUser.id, userRole)
          : await createCareAdvisorProfile(caProfilePayload, thisUser.id, userRole);
      }

      if (reviewProfile) {
        const submittedTagIds = (profileData.client_tag_ids || []).map(String).sort();
        const existingTagIds = (thisUser.clients || []).map((c) => String(c.id)).sort();
        const submittedDivisionIds = (profileData.client_division_ids || []).map(String).sort();
        const existingDivisionIds = (thisUser.divisions || []).map((d) => String(d.id)).sort();
        const tagsChanged = JSON.stringify(submittedTagIds) !== JSON.stringify(existingTagIds);
        const divisionsChanged = JSON.stringify(submittedDivisionIds) !== JSON.stringify(existingDivisionIds);
        // A locked user's tags go back exactly as they hold them: the server lets an unchanged set
        // through and scopes only the divisions this save changes.
        if (clientTagsLocked ? divisionsChanged : tagsChanged || divisionsChanged) {
          const client_ids = clientTagsLocked
            ? (thisUser.clients || []).map((c) => c.id)
            : submittedTagIds.map(Number).filter((n) => !Number.isNaN(n));
          const client_division_ids = submittedDivisionIds.map(Number).filter((n) => !Number.isNaN(n));
          await updateUserClientTag(thisUser.id, { client_ids, client_division_ids });
        }
        if (canEditNetwork && (profileData.network ?? null) !== (thisUser.network ?? null)) {
          await updateUserNetwork(thisUser.id, profileData.network ?? null, userInfo.role.name);
        }
        updatedUser = await getUserById(thisUser.id);
      } else {
        updatedUser = await getCurrentProfile('CareAdvisor');
      }

      setThisUser(updatedUser);

      const savedFormValues = getValues();
      setMyProfileForm({ ...savedFormValues });
      reset(savedFormValues);
      setAvatarAction('');
      setAvatarFile(null);
      setAvatarPayload(null);
      // Clear the dirty flag so the unsaved-changes prompt doesn't reappear for a save that
      // already succeeded; the parent's post-save `setThisUser` re-triggers hydration below,
      // which the `hasUserEditedFormRef` guard above would otherwise skip.
      hasUserEditedFormRef.current = false;
      setHasEditedSinceSave(false);

      if (!reviewProfile) {
        dispatch(setCareAdvisorMyProfile({ ...savedFormValues }));
        dispatch(setUser({ userInfo: updatedUser }));
      }

      toast.success('Your changes have been saved');
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const onInvalid = (errorsList: FieldErrors): void => {
    const error = Object.values(errorsList)[0];
    if (error && 'message' in error) {
      toast.error(error.message as string, { className: 'fs-unmask' });
    }
  };

  const onSubmit = async (profileData: CAMyProfileSubmit): Promise<void> => {
    if (isManagedByD1) {
      toast.error('This profile is managed by Dentistry.One and cannot be edited.', { className: 'fs-unmask' });
      return;
    }
    if (hasUnfilledRequiredDivisionGroup(divisionGroups, profileData.client_division_ids || [])) {
      toast.error('Select a division.', { className: 'fs-unmask' });
      return;
    }
    await callSaveProfile(profileData);
  };

  const setTextValue = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, name: string): void => {
    const { value } = e.target;
    setValue(name as keyof CAMyProfileSubmit, value, { shouldValidate: true });
  };

  useEffect(() => {
    if (hasUserEditedFormRef.current) return;

    isHydratingFormRef.current = true;
    try {
      if (Object.keys(myProfileForm).length > 1) {
        Object.keys(myProfileForm).forEach((key: unknown) => {
          setValue(key as keyof CAMyProfileSubmit, myProfileForm[key as keyof CAMyProfileSubmit]);
          if (thisUser.picture_media_id) {
            setValue('avatar', String(thisUser.picture_media_id), { shouldValidate: true });
          }
        });
        if (thisUser?.email) {
          setValue('email', thisUser.email, { shouldValidate: true });
        }
        setLoading(false);
      }
    } finally {
      isHydratingFormRef.current = false;
    }
  }, [myProfileForm]);

  useEffect(() => {
    if (image) triggerProfileAvatar(image);
  }, [image]);

  const onResetPassword = (): void => {
    setResetPasswordConfirmation({
      isOpen: true,
      userState: thisUser.state || '',
    });
  };

  const phoneInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className={`${styles.caProfileContainer} ${reviewProfile ? styles.reviewProfileContainer : ''}`}>
      {reviewProfile && (
        <>
          <div className={styles.reviewHeaderShell}>
            <button type="button" className={styles.reviewBackButton} onClick={() => navigate(-1)}>
              <IoChevronBack aria-hidden />
              Back
            </button>

            <header className={`${collectorShellStyles.collectorEditUserHeader} ${styles.reviewHeader}`}>
              <div className={collectorShellStyles.collectorEditUserHeaderIdentity}>
                <ProfileImage
                  className={styles.headerProfileImage}
                  reviewProfile={reviewProfile}
                  id={userId}
                  setCurrentAvatarURL={setCurrentAvatarURL}
                  setAvatarFile={setAvatarFile}
                  setAvatarPayload={setAvatarPayload}
                  setAvatarAction={setAvatarAction}
                  triggerProfileAvatar={triggerProfileAvatar}
                  setImage={setImage}
                  image={image}
                  errors={errors}
                  size="80px"
                  isUpload={!isManagedByD1}
                  disableDelete
                  givingUserProfile={
                    {
                      first_name: thisUser.basic_info?.first_name || headerDisplayName.split(' ')[0] || '',
                      last_name:
                        thisUser.basic_info?.last_name || headerDisplayName.split(' ').slice(1).join(' ') || '',
                    } as ProfileImageGivingUserProfile
                  }
                />
                <div className={`${collectorShellStyles.collectorEditUserHeaderText} ${styles.reviewHeaderText}`}>
                  <h1 className={`${collectorShellStyles.collectorEditUserHeaderName} ${styles.reviewHeaderName}`}>
                    {headerDisplayName}
                  </h1>
                  <div className={`${collectorShellStyles.userStatusRow} ${styles.reviewStatusRow}`}>
                    <span className={`${collectorShellStyles.userStatusLabel} ${styles.reviewStatusLabel}`}>
                      User status:
                    </span>
                    <button
                      type="button"
                      className={`${collectorShellStyles.statusPillReadOnly} ${statusPillClass} ${styles.statusPillButton}`}
                      aria-controls={statusMenuAnchor ? 'ca-review-user-status-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={statusMenuAnchor ? 'true' : undefined}
                      onClick={openStatusMenu}
                      disabled={isManagedByD1}
                    >
                      {collectorDisplayStatus}
                      <IoChevronDown aria-hidden />
                    </button>
                    <Menu
                      id="ca-review-user-status-menu"
                      anchorEl={statusMenuAnchor}
                      open={Boolean(statusMenuAnchor)}
                      onClose={closeStatusMenu}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                      {statusOptions.map((status) => (
                        <MenuItem key={status.id} onClick={() => handleStatusClick(status.id)}>
                          <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                            {status.name}
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </Menu>
                  </div>
                </div>
              </div>
              <div className={collectorShellStyles.collectorEditUserHeaderActions}>
                {canCreateCase && (
                  <button
                    type="button"
                    className={collectorShellStyles.collectorEditUserCreateCaseLink}
                    onClick={() => navigate('/review-profile/patient/select')}
                  >
                    <IoAdd aria-hidden />
                    Create new case
                  </button>
                )}
                {thisUser.role.name !== UserRoleName.Dependent && !isManagedByD1 && (
                  <>
                    <IconButton
                      aria-label="More actions"
                      aria-controls={userHeaderMenuAnchor ? 'ca-review-user-header-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={userHeaderMenuAnchor ? 'true' : undefined}
                      onClick={openUserHeaderMenu}
                    >
                      <IoEllipsisVertical />
                    </IconButton>
                    <Menu
                      id="ca-review-user-header-menu"
                      anchorEl={userHeaderMenuAnchor}
                      open={Boolean(userHeaderMenuAnchor)}
                      onClose={closeUserHeaderMenu}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <MenuItem
                        onClick={() => {
                          closeUserHeaderMenu();
                          void inviteUser();
                        }}
                        disabled={
                          thisUser.role.name === UserRoleName.Dependent ||
                          ['Active', 'Pending', 'Deactivated'].includes(thisUser?.status) ||
                          isInvitedUser
                        }
                      >
                        <ListItemIcon>
                          <IoAdd />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                          Invite user
                        </ListItemText>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          closeUserHeaderMenu();
                          setResetPasswordConfirmation({ isOpen: true, userState: 'Active' });
                        }}
                        disabled={
                          ['Deactivated', 'Not Invited', 'Invited'].includes(thisUser?.status) || resetEmailSent
                        }
                      >
                        <ListItemIcon>
                          <HttpsOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                          Reset password
                        </ListItemText>
                      </MenuItem>
                      {showUnlockAccount && (
                        <MenuItem
                          onClick={() => {
                            closeUserHeaderMenu();
                            openUnlockConfirm();
                          }}
                          disabled={!thisUser?.email || isUnlocking}
                        >
                          <ListItemIcon>
                            <LockOpenOutlinedIcon />
                          </ListItemIcon>
                          <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                            Unlock account
                          </ListItemText>
                        </MenuItem>
                      )}
                    </Menu>
                  </>
                )}
              </div>
            </header>

            <Alert
              severity="info"
              className={addNewUserStyles.patientProfileNotice}
              sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
            >
              {isManagedByD1 ? (
                <>
                  <strong>{headerDisplayName}</strong> belongs to the {getNetworkLabel(thisUser.network)}. Their profile
                  is managed by Dentistry.One and can only be viewed here.
                </>
              ) : (
                <>
                  You are editing <strong>{headerDisplayName}</strong>&apos;s profile.
                </>
              )}
            </Alert>
          </div>

          {displayEmailModal && (
            <AddEmailModal
              isOpen={displayEmailModal}
              onClose={() => setDisplayEmailModal(false)}
              userId={userId || ''}
              thisUser={thisUser}
              setThisUser={setThisUser}
              inviteUser={inviteUser}
            />
          )}

          <ConfirmationModal
            isOpen={unlockConfirmOpen}
            title="Unlock account"
            content={`Are you sure you want to unlock ${headerDisplayName}'s account? This clears the account-lockout state so they can sign in again.`}
            onClose={closeUnlockConfirm}
            onConfirm={() => void handleUnlockAccount()}
            isSubmitDisabled={isUnlocking}
          />
        </>
      )}
      <form ref={formRef} className={styles.formWrapper} onSubmit={handleSubmit(onSubmit, onInvalid)}>
        {!reviewProfile && (
          <div className={styles.avatarRow}>
            <ProfileImage
              reviewProfile={reviewProfile}
              id={userId}
              setCurrentAvatarURL={setCurrentAvatarURL}
              setAvatarFile={setAvatarFile}
              setAvatarPayload={setAvatarPayload}
              setAvatarAction={setAvatarAction}
              triggerProfileAvatar={triggerProfileAvatar}
              setImage={setImage}
              image={image}
              errors={errors}
              size="72px"
              isUpload={false}
            />
            <Button
              variant="text"
              component="label"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '15px',
                borderRadius: '32px',
                color: 'var(--primary-color)',
              }}
            >
              Upload profile picture
              <span style={{ color: 'var(--primary-color)', marginLeft: '2px', opacity: 0.7 }}> *</span>
              <input
                type="file"
                hidden
                accept=".png,.jpg,.jpeg,.jfif,.pjpeg,.pjp,.heic"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setAvatarAction('Upload');
                    const uploadedPic = URL.createObjectURL(file);
                    setImage(uploadedPic);
                    setAvatarPayload({
                      files: [{ filename: file.name, mimetype: file.type }],
                    });
                    setAvatarFile(file);
                  }
                }}
              />
            </Button>
          </div>
        )}
        {!reviewProfile && errors?.profile_img?.message && (
          <div className={styles.errorText}>{errors.profile_img.message}</div>
        )}

        <div className={styles.nameRow}>
          <CustomTextField
            name="first_name"
            label="First name"
            required
            value={getValues('first_name')}
            onChange={(e) => setTextValue(e, 'first_name')}
            errors={errors}
            disabled={isManagedByD1}
          />
          <CustomTextField
            name="middle_name"
            label="Middle name (optional)"
            value={getValues('middle_name')}
            onChange={(e) => setTextValue(e, 'middle_name')}
            errors={errors}
            disabled={isManagedByD1}
          />
          <CustomTextField
            name="last_name"
            label="Last name"
            required
            value={getValues('last_name')}
            onChange={(e) => setTextValue(e, 'last_name')}
            errors={errors}
            disabled={isManagedByD1}
          />
        </div>
        <CustomTextField
          name="email"
          label="Email address"
          value={getValues('email')}
          onChange={(e) => setTextValue(e, 'email')}
          errors={errors}
          disabled={isReadOnly || isManagedByD1}
        />
        <div className={styles.contactRow}>
          <Controller
            name="phone_number"
            control={control}
            render={({ field }) => (
              <PhoneInput
                name="phone_number"
                label="Phone number"
                required
                value={field.value as E164Number}
                onChange={(val) => {
                  setValue('phone_number', val as string, { shouldValidate: true });
                }}
                errors={errors}
                disabled={isManagedByD1}
                ref={phoneInputRef}
              />
            )}
          />
          <CustomTextField
            name="zip_code"
            label="Zip code"
            required
            value={getValues('zip_code')}
            onChange={(e) => setTextValue(e, 'zip_code')}
            errors={errors}
            disabled={isManagedByD1}
          />
        </div>

        <Controller
          name="language"
          control={control}
          render={({ field }) => (
            <MultiSelectInputMUI
              name="language"
              label="Languages"
              required
              options={languageSelectOptions}
              value={selectedLanguageIds}
              onChange={(newIds: string[]) => {
                const selectedOpts = languageOptions.filter((opt) => newIds.includes(opt.id.toString()));
                field.onChange(selectedOpts);
              }}
              errors={errors}
              withId
              styledTag={false}
              displayCheckbox
              disabled={isManagedByD1}
            />
          )}
        />

        <MuiLongTextField
          name="biography"
          label="Biography"
          required
          value={getValues('biography')}
          onChange={(e) => setTextValue(e, 'biography')}
          multiline
          minRows={1}
          maxRows={3}
          inputProps={{
            maxLength: undefined,
            style: {
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              overflowX: 'hidden',
            },
          }}
          errors={errors}
          disabled={isManagedByD1}
        />

        {reviewProfile &&
        !isManagedByD1 &&
        !clientTagsLocked &&
        (userInfo.role.name === UserRoleName.Admin ||
          userInfo.role.name === UserRoleName.SuperAdmin ||
          userInfo.role.name === UserRoleName.OrganizationAdmin) ? (
          <MultiSelectInputMUI
            label="Client tags"
            name="client_tag_ids"
            withId
            styledTag
            required={clientTagsRequired}
            value={watch('client_tag_ids') || []}
            onChange={(ids: string[]) => {
              setValue('client_tag_ids', ids, { shouldValidate: true });
              const allowedDivisionIds = getAllowedDivisionIds(
                clientTagCatalog,
                ids,
                (thisUser.divisions || []).map((d) => ({ id: String(d.id), value: d.id, label: d.name })),
              );
              setValue(
                'client_division_ids',
                (watch('client_division_ids') || []).filter((id) => allowedDivisionIds.has(String(id))),
                { shouldValidate: true },
              );
            }}
            options={clientTagOptions}
            errors={errors}
          />
        ) : (
          <div className={styles.clientTagsContainer}>
            <div className={styles.clientTagsLabel}>Client tags</div>
            <div className={styles.clientTagsChips}>
              {clientTags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  variant="outlined"
                  size="medium"
                  sx={{
                    borderRadius: '100px',
                    borderColor: 'rgba(23, 36, 51, 0.16)',
                    color: 'rgba(23, 36, 51, 0.45)',
                    pointerEvents: 'none',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '13px',
                  }}
                />
              ))}
              {clientTags.length === 0 && <span className={styles.clientTagsEmpty}>No client tags assigned</span>}
            </div>
          </div>
        )}

        {isReadOnly || isManagedByD1 ? (
          <>
            {assignedDivisionGroups.map((group) => (
              <div className={styles.clientTagsContainer} key={group.clientTagId || group.clientTagName}>
                <div className={styles.clientTagsLabel}>
                  {group.clientTagName ? `${group.clientTagName} - Division(s)` : 'Division(s)'}
                </div>
                <div className={styles.clientTagsChips}>
                  {group.options.map((division) => (
                    <Chip
                      key={division.id}
                      label={division.label}
                      variant="outlined"
                      size="medium"
                      sx={{
                        borderRadius: '100px',
                        borderColor: 'rgba(23, 36, 51, 0.16)',
                        color: 'rgba(23, 36, 51, 0.45)',
                        pointerEvents: 'none',
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '13px',
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
            {assignedDivisionGroups.length === 0 && (
              <div className={styles.clientTagsContainer}>
                <div className={styles.clientTagsLabel}>Division(s)</div>
                <div className={styles.clientTagsChips}>
                  <span className={styles.clientTagsEmpty}>No divisions assigned</span>
                </div>
              </div>
            )}
          </>
        ) : reviewProfile &&
          (userInfo.role.name === UserRoleName.Admin ||
            userInfo.role.name === UserRoleName.SuperAdmin ||
            userInfo.role.name === UserRoleName.OrganizationAdmin) &&
          (watchedFields.client_tag_ids || []).length > 0 &&
          divisionGroups.length > 0 ? (
          <>
            {divisionGroups.map((group) => (
              <MultiSelectInputMUI
                key={group.clientTagId}
                label={`${group.clientTagName} - Division(s)`}
                name="client_division_ids"
                withId
                styledTag
                required={group.required}
                value={(watch('client_division_ids') || []).filter((id) =>
                  group.options.some((option) => String(option.id) === String(id)),
                )}
                onChange={(ids: string[]) => setGroupDivisionIds(group, ids)}
                options={group.options}
                errors={errors}
              />
            ))}
          </>
        ) : null}
        {reviewProfile && canEditNetwork ? (
          <MUISelectInput
            label="Network"
            name="network"
            value={watch('network') || ''}
            onChange={(value: string) => {
              setValue('network', value || null, { shouldValidate: true });
            }}
            options={networkOptions.map((option) => ({ id: option.code, value: option.code, label: option.label }))}
            errors={errors}
          />
        ) : null}

        {!isManagedByD1 && (
          <div className={styles.buttonRow}>
            <Button
              type="submit"
              variant="contained"
              className={styles.saveButton}
              disabled={!hasChanges || isSavingProfile}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '15px',
                borderRadius: '32px',
                px: '22px',
                py: '8px',
                backgroundColor: 'var(--primary-color)',
                '&:hover': {
                  backgroundColor: '#245fa0',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#bdbdbd',
                  color: '#fff',
                },
              }}
            >
              Save changes
            </Button>
            <Button
              type="button"
              variant="text"
              onClick={onResetPassword}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '15px',
                borderRadius: '32px',
                color: '#d32f2f',
                py: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.04)',
                },
              }}
            >
              Reset password
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default EditProfile;
